import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../hooks/useAuth';
import { useTour } from '../context/TourContext';
import Navbar from '../components/Navbar';

interface EmailPreferences {
  marketing_emails: boolean;
  product_updates: boolean;
  flow_change_notifications: boolean;
  sync_failure_alerts: boolean;
  subscription_reminders: boolean;
}

export default function PreferencesPage() {
  const { user } = useAuth();
  const { resetTourSettings, hasHiddenTourPermanently } = useTour();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tourReset, setTourReset] = useState(false);
  const [preferences, setPreferences] = useState<EmailPreferences>({
    marketing_emails: true,
    product_updates: true,
    flow_change_notifications: true,
    sync_failure_alerts: true,
    subscription_reminders: true,
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchPreferences();
  }, [user, navigate]);

  const fetchPreferences = async () => {
    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user!.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching preferences:', error);
      }

      if (data) {
        setPreferences({
          marketing_emails: data.marketing_emails ?? true,
          product_updates: data.product_updates ?? true,
          flow_change_notifications: data.flow_change_notifications ?? true,
          sync_failure_alerts: data.sync_failure_alerts ?? true,
          subscription_reminders: data.subscription_reminders ?? true,
        });
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key: keyof EmailPreferences) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user!.id,
          ...preferences,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      // Show success message
      const successDiv = document.createElement('div');
      successDiv.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successDiv.textContent = 'Preferences saved successfully!';
      document.body.appendChild(successDiv);
      
      setTimeout(() => {
        successDiv.remove();
      }, 3000);

    } catch (error) {
      console.error('Error saving preferences:', error);
      alert('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-semibold text-gray-900">Email Preferences</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage how you receive communications from FlowDocs
            </p>
          </div>

          <div className="px-6 py-6 space-y-6">
            {/* Operational Emails */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Operational Emails</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="flow-changes"
                      type="checkbox"
                      checked={preferences.flow_change_notifications}
                      onChange={() => handleToggle('flow_change_notifications')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="flow-changes" className="font-medium text-gray-700">
                      Flow Change Notifications
                    </label>
                    <p className="text-sm text-gray-500">
                      Get notified when important changes are detected in your Salesforce flows
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="sync-failures"
                      type="checkbox"
                      checked={preferences.sync_failure_alerts}
                      onChange={() => handleToggle('sync_failure_alerts')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="sync-failures" className="font-medium text-gray-700">
                      Sync Failure Alerts
                    </label>
                    <p className="text-sm text-gray-500">
                      Receive alerts when sync operations fail or require attention
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="subscription-reminders"
                      type="checkbox"
                      checked={preferences.subscription_reminders}
                      onChange={() => handleToggle('subscription_reminders')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="subscription-reminders" className="font-medium text-gray-700">
                      Subscription Reminders
                    </label>
                    <p className="text-sm text-gray-500">
                      Important notices about your subscription and billing
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Tour Settings */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Tour Settings</h2>
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-gray-700">Guided Tour</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {hasHiddenTourPermanently
                          ? "You've hidden the tour button. Click reset to show it again."
                          : "The tour button appears in the bottom-left corner of the screen."}
                      </p>
                      {tourReset && (
                        <p className="text-sm text-green-600 mt-2">
                          ✓ Tour settings have been reset. The tour button will appear again.
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        resetTourSettings();
                        setTourReset(true);
                        setTimeout(() => setTourReset(false), 5000);
                      }}
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700"
                    >
                      Reset Tour
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Marketing Emails */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Marketing Communications</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="product-updates"
                      type="checkbox"
                      checked={preferences.product_updates}
                      onChange={() => handleToggle('product_updates')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="product-updates" className="font-medium text-gray-700">
                      Product Updates
                    </label>
                    <p className="text-sm text-gray-500">
                      News about new features, improvements, and product roadmap
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="marketing"
                      type="checkbox"
                      checked={preferences.marketing_emails}
                      onChange={() => handleToggle('marketing_emails')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="marketing" className="font-medium text-gray-700">
                      Tips & Best Practices
                    </label>
                    <p className="text-sm text-gray-500">
                      Educational content to help you get the most out of FlowDocs
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-4">
                Note: You cannot opt out of critical service emails such as security notices and legal updates.
              </p>
              <div className="flex justify-end">
                <button
                  onClick={() => navigate(-1)}
                  className="mr-3 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Preferences'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}